import { Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout, AuthLayout } from '@/components/layout';
import { ErrorBoundary } from '@/components/errors';
import { Toaster } from '@/components/ui';
import { UpgradePrompt } from '@/components/billing';
import ProtectedRoute from '@/components/ProtectedRoute';
import RootRedirect from '@/components/RootRedirect';
import { useUpgradePromptStore } from '@/store/upgradePromptStore';
import { useEventSource } from '@/hooks/useEventSource';
import {
  LoginPage,
  SignupWizard,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  MfaChallengePage,
  MagicLinkPage,
  MagicLinkVerifyPage,
  OAuthCallbackPage,
  AccountsPage,
  AllocationPage,
  BillingPage,
  BillingSuccess,
  CategoriesPage,
  ConnectionsPage,
  CompetitorImportWizard,
  DashboardPage,
  DocumentImportWizard,
  DocumentsPage,
  FlowPage,
  InternalErrorPage,
  NotFoundPage,
  PrivacyPolicyPage,
  SecurityPage,
  SettingsPage,
  TermsOfServicePage,
  TransactionsPage,
  AboutPage,
  PlanningPage,
} from '@/pages';
import { AcceptInvitation } from '@/pages/invite';

/**
 * Global Upgrade Prompt
 * Shown when any API call returns 402 Payment Required
 * WP-BILLING-4: Phase 3 - Global 402 Handler
 */
function GlobalUpgradePrompt() {
  const { isOpen, featureLabel, hide } = useUpgradePromptStore();

  return (
    <UpgradePrompt
      isOpen={isOpen}
      onClose={hide}
      title="Upgrade Required"
      message={
        featureLabel
          ? `The ${featureLabel} feature requires a Pro subscription. Upgrade to unlock this and all premium features.`
          : 'This feature requires a Pro subscription. Upgrade to unlock all premium features.'
      }
    />
  );
}

/**
 * SSE Event Source Provider
 * Establishes real-time connection to backend and routes events to React Query
 * WP-RT-2: Frontend Real-Time Integration
 */
function EventSourceProvider() {
  useEventSource();
  return null;
}

/**
 * Main App component with routing configuration
 *
 * Wrapped in ErrorBoundary to catch and handle React errors gracefully.
 * Error logging is configured to avoid PII exposure.
 */
export default function App() {
  return (
    <ErrorBoundary>
      <Toaster />
      <GlobalUpgradePrompt />
      <EventSourceProvider />
      <Routes>
        {/* Public auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<SignupWizard />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/mfa" element={<MfaChallengePage />} />
          <Route path="/magic-link" element={<MagicLinkPage />} />
          <Route path="/magic-link/verify/:token" element={<MagicLinkVerifyPage />} />
          {/* WP-AUTH-7: OAuth callback for Microsoft and other redirect-based OAuth providers */}
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        </Route>

        {/* Protected routes with main layout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/allocation" element={<AllocationPage />} />
          <Route path="/planning" element={<PlanningPage />} />
          {/* WP-PLAN-1: /retirement was the old path. `replace` so the old URL
              does not linger in history - same pattern as RootRedirect.tsx:32
              and ProtectedRoute.tsx:31. Keep this redirect: external links,
              bookmarks and the spec's older examples still point at it. */}
          <Route path="/retirement" element={<Navigate to="/planning" replace />} />
          <Route path="/connections" element={<ConnectionsPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/flow" element={<FlowPage />} />
          <Route path="/flow/:flowId" element={<FlowPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/:tab" element={<SettingsPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/import" element={<DocumentImportWizard />} />
          {/* WP-IMPORT-1: import from Mint, YNAB or Quicken; hands off to /import for review */}
          <Route path="/import/apps" element={<CompetitorImportWizard />} />
        </Route>

        {/* Invitation acceptance page (works for both authenticated and unauthenticated users) */}
        <Route path="/invite/:token" element={<AcceptInvitation />} />

        {/* Error pages */}
        <Route path="/error" element={<InternalErrorPage />} />

        {/* Public legal pages */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/about" element={<AboutPage />} />

        {/* Root path: Landing page for visitors, dashboard redirect for users */}
        <Route path="/" element={<RootRedirect />} />

        {/* 404 catch-all route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  );
}
